/*
  bs-mascot-fx.js — Sir Plus header animations.

  1. Add-to-cart POW!  When a product form adds to the cart (Dawn publishes
     PUB_SUB_EVENTS.cartUpdate with source 'product-form'), Sir Plus flies in
     from the right, punches the cart icon, and the count bubble pops.
  2. Header hello  Once per visit by default, Sir Plus shows up at the search
     bar. Two styles: "price" (a $ swells up over the bar, he flies in, punches
     it back down to size, gives a thumbs-up and a word, then flies off) and
     "peek" (he rises from behind the bar, waves, says hello and ducks back
     down). Desktop only: below 990px the search bar sits under the logo row,
     with no room around it.
  3. Price punch  On a product page whose selected variant has a compare-at
     price, "their price" swells up big and red, Sir Plus flies in and punches
     it back down to its strikethrough, the "You save" chip pops and he says
     how much he saved you. Phones too. These pages skip the header hello, so
     he only shows up once per page.

  Settings come from the JSON block rendered by snippets/bs-mascot-fx.liquid
  (Header section > "Sir Plus animations"). Nothing runs for visitors who ask
  for reduced motion, and every element is aria-hidden decoration.
*/
(() => {
  if (window.bsMascotFx) return;
  window.bsMascotFx = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // width / height of each sprite in assets/bs-sirplus-*.webp
  const RATIO = { wave: 150 / 193, punch: 150 / 201, thumbs: 150 / 224 };
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  let cfg = readConfig();

  function readConfig() {
    const el = document.getElementById('bs-mascot-fx-config');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function inViewport(el) {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
  }

  function sprite(src, className) {
    const img = new Image();
    img.alt = '';
    img.decoding = 'async';
    if (className) img.className = className;
    img.src = src;
    return img;
  }

  function loaded(img) {
    if (img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      img.onload = img.onerror = resolve;
    });
  }

  /* ---------- 1. Add-to-cart POW! ---------- */

  let powPlaying = false;

  function burstMarkup(label) {
    const points = [];
    for (let i = 0; i < 24; i++) {
      const r = i % 2 ? 31 : 50;
      const a = (i / 24) * Math.PI * 2 - Math.PI / 2;
      points.push(`${(50 + r * Math.cos(a)).toFixed(1)},${(50 + r * Math.sin(a) * 0.84).toFixed(1)}`);
    }
    const text = label.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
    const squeeze = label.length > 5 ? ' textLength="66" lengthAdjust="spacingAndGlyphs"' : '';
    return (
      `<svg viewBox="0 0 100 100" aria-hidden="true">` +
      `<polygon points="${points.join(' ')}" fill="#e6343c" stroke="#0a0a0a" stroke-width="3.5" stroke-linejoin="round"/>` +
      `<text x="50" y="59" text-anchor="middle" font-size="25" fill="#fff" stroke="#0a0a0a" stroke-width="1.2" paint-order="stroke"${squeeze}>${text}</text>` +
      `</svg>`
    );
  }

  async function playPow(cart) {
    powPlaying = true;
    const layer = document.createElement('div');
    layer.className = 'bs-mfx-layer';
    layer.setAttribute('aria-hidden', 'true');

    try {
      const r = cart.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const small = window.innerWidth < 750;
      const h = small ? 54 : 70;
      const size = small ? 64 : 82;

      // The fist sits ~28% across and ~41% down the punch pose: land it on the cart.
      const left = cx - h * RATIO.punch * 0.28;
      const top = cy - h * 0.41;
      const hero = sprite(cfg.sprites.punch);
      Object.assign(hero.style, { height: `${h}px`, left: `${left}px`, top: `${top}px` });

      // Burst up and to the left of the impact so the fist doesn't cover the word.
      const burst = document.createElement('div');
      burst.className = 'bs-mfx-burst';
      burst.innerHTML = burstMarkup(cfg.powText || 'POW!');
      Object.assign(burst.style, {
        width: `${size}px`,
        height: `${size}px`,
        left: `${Math.max(4, cx - size * 0.82)}px`,
        top: `${Math.max(4, cy - size * 0.7)}px`,
      });

      layer.append(burst, hero);
      document.body.append(layer);
      await loaded(hero);

      const offscreen = window.innerWidth - left + 10;
      await hero.animate(
        [
          { transform: `translate(${offscreen}px, -24px) rotate(-10deg)` },
          { transform: 'translate(-6px, 0) rotate(2deg)', offset: 0.75 },
          { transform: 'none' },
        ],
        { duration: 340, easing: 'cubic-bezier(.3,1.3,.5,1)', fill: 'both' }
      ).finished;

      burst.animate(
        [
          { opacity: 0, transform: 'scale(.2) rotate(-25deg)' },
          { opacity: 1, transform: 'scale(1.2) rotate(6deg)', offset: 0.6 },
          { opacity: 1, transform: 'none' },
        ],
        { duration: 300, easing: 'ease-out', fill: 'both' }
      );
      cart.animate(
        [
          { transform: 'none' },
          { transform: 'translate(-3px, 1px) rotate(-8deg)' },
          { transform: 'translate(2px, -1px) rotate(5deg)' },
          { transform: 'none' },
        ],
        { duration: 260 }
      );
      // cart-notification.js re-renders the bubble's contents right after the
      // add, so look it up late enough to pop the new count.
      setTimeout(() => {
        const bubble = cart.querySelector('.cart-count-bubble');
        if (bubble) {
          bubble.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.6)' }, { transform: 'scale(1)' }], {
            duration: 420,
            easing: 'ease-out',
          });
        }
      }, 160);

      await sleep(850);
      burst.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(1.3)' }], { duration: 220, fill: 'forwards' });

      // Thumbs-up for the exit beat, feet kept on the same line.
      const th = h * 1.1;
      hero.src = cfg.sprites.thumbs;
      Object.assign(hero.style, { height: `${th}px`, top: `${top + h - th}px` });
      await sleep(550);

      await hero.animate(
        [
          { transform: 'none' },
          { transform: 'translateY(-10px)', offset: 0.3 },
          { transform: `translate(${offscreen}px, -40px) rotate(8deg)` },
        ],
        { duration: 420, easing: 'ease-in', fill: 'forwards' }
      ).finished;
    } finally {
      layer.remove();
      powPlaying = false;
    }
  }

  function onCartUpdate(event) {
    if (!cfg || !cfg.pow || powPlaying || reduceMotion.matches) return;
    if (!event || event.source !== 'product-form') return;
    const cart = document.getElementById('cart-icon-bubble');
    if (!cart) return;
    if (inViewport(cart)) {
      playPow(cart);
      return;
    }
    // Scrolled down a product page, so the header is off-screen. The cart
    // popup focuses itself once it slides in, which jumps the page back up to
    // the header: play as soon as that scroll brings the cart into view, and
    // give up if it hasn't within a few seconds (the popup only focuses after
    // its slide-in finishes, which slow devices can stretch well past 2s).
    const stop = new AbortController();
    const timer = setTimeout(() => stop.abort(), 5000);
    window.addEventListener(
      'scroll',
      () => {
        if (powPlaying || !inViewport(cart)) return;
        stop.abort();
        clearTimeout(timer);
        requestAnimationFrame(() => playPow(cart));
      },
      { passive: true, signal: stop.signal }
    );
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  }

  /* ---------- 2. Header hello ---------- */

  // The hello and the price punch each keep their own "seen" mark, on the
  // same "how often" setting, so a hello on the home page doesn't use up the
  // punch on the first sale product.
  const SEEN_KEY = 'bsMascotHello';
  let helloPlaying = false;

  function due(key) {
    if (cfg.designMode || cfg.helloEvery === 'always') return true;
    try {
      if (cfg.helloEvery === 'week') {
        return Date.now() - Number(localStorage.getItem(key) || 0) > 7 * 864e5;
      }
      return !sessionStorage.getItem(key);
    } catch (e) {
      return false; // storage blocked: better silent than greeting on every page
    }
  }

  function markSeen(key) {
    try {
      if (cfg.helloEvery === 'week') localStorage.setItem(key, String(Date.now()));
      else sessionStorage.setItem(key, '1');
    } catch (e) {}
  }

  async function playHello() {
    if (!cfg || !cfg.hello || helloPlaying || reduceMotion.matches) return;
    if (window.innerWidth < 990 || !due(SEEN_KEY)) return;
    if (cfg.price && saleOnPage()) return; // the price punch has this page

    const bar = document.querySelector('.bs-header .bs-search__form');
    const wrap = bar && bar.closest('.bs-search');
    const header = bar && bar.closest('.header');
    if (!wrap || !header || !inViewport(bar) || wrap.contains(document.activeElement)) return;

    // Yellow between the top of the header row and the bar: the peek's whole
    // stage, and where the price patrol's speech bubble goes.
    const room = Math.round(bar.getBoundingClientRect().top - header.getBoundingClientRect().top);
    if (room < 18) return;

    helloPlaying = true;
    markSeen(SEEN_KEY);

    // Any interaction with the search bar, or scrolling away, ends it early.
    const done = new AbortController();
    const finish = () => done.abort();
    wrap.addEventListener('focusin', finish, { signal: done.signal });
    wrap.addEventListener('pointerdown', finish, { signal: done.signal });
    window.addEventListener('scroll', finish, { passive: true, signal: done.signal });
    const aborted = new Promise((resolve) => done.signal.addEventListener('abort', resolve, { once: true }));
    const until = (promise) => Promise.race([promise, aborted]);

    try {
      const stage = { bar, wrap, header, room, done, until };
      if (cfg.helloStyle === 'peek') await peekaboo(stage);
      else await pricePatrol(stage);
    } finally {
      done.abort();
      helloPlaying = false;
    }
  }

  // Peek-a-boo: Sir Plus rises from behind the bar, waves and says hello, then
  // ducks back down. He is sized so his hand and face fill the room above it.
  async function peekaboo({ wrap, room, done, until }) {
    const h = Math.min(84, Math.max(40, room * 2));
    const box = document.createElement('div');
    box.className = 'bs-mfx-peek';
    box.setAttribute('aria-hidden', 'true');
    Object.assign(box.style, { height: `${room}px`, width: `${Math.ceil(h * RATIO.wave) + 4}px` });
    const hero = sprite(cfg.sprites.wave, 'bs-mfx-peek__hero');
    hero.style.height = `${h}px`;
    box.append(hero);

    const bubble = document.createElement('div');
    bubble.className = 'bs-mfx-bubble';
    bubble.setAttribute('aria-hidden', 'true');
    bubble.textContent = cfg.helloText || 'Hey there!';
    wrap.append(box, bubble);
    bubble.style.left = `${box.offsetLeft + box.offsetWidth + 14}px`;
    bubble.style.bottom = `calc(100% + ${Math.max(0, Math.round((room - bubble.offsetHeight) / 2))}px)`;

    let wave;
    try {
      await until(loaded(hero));
      if (!done.signal.aborted) {
        await until(
          hero.animate(
            [{ transform: 'translateY(100%)' }, { transform: 'translateY(-5%)', offset: 0.75 }, { transform: 'translateY(0)' }],
            { duration: 520, easing: 'cubic-bezier(.2,.9,.3,1.3)', fill: 'forwards' }
          ).finished
        );
      }
      if (!done.signal.aborted) {
        wave = hero.animate([{ transform: 'rotate(-6deg)' }, { transform: 'rotate(6deg)' }], {
          duration: 300,
          iterations: 8,
          direction: 'alternate',
          easing: 'ease-in-out',
        });
        bubble.animate(
          [
            { opacity: 0, transform: 'scale(.3)' },
            { opacity: 1, transform: 'scale(1.08)', offset: 0.7 },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 300, easing: 'ease-out', fill: 'forwards' }
        );
        await until(sleep(2800));
      }

      if (wave) wave.cancel();
      bubble.animate([{ opacity: getComputedStyle(bubble).opacity }, { opacity: 0 }], { duration: 160, fill: 'forwards' });
      await hero.animate(
        [{ transform: getComputedStyle(hero).transform }, { transform: 'translateY(100%)' }],
        { duration: done.signal.aborted ? 200 : 380, easing: 'ease-in', fill: 'forwards' }
      ).finished;
    } finally {
      box.remove();
      bubble.remove();
    }
  }

  // Price patrol: a $ over the search bar swells up like a price on the rise,
  // Sir Plus flies in and punches it back down to size, gives a thumbs-up and
  // a word, then flies off to the next one. It plays in the same fixed layer
  // as the POW!, so the $ can bulge past the bar while it's at its worst.
  const PRICE = { low: '#1f9d55', rising: '#f59e0b', high: '#e6343c' };

  function dollarMarkup() {
    return (
      `<svg viewBox="0 0 60 100" aria-hidden="true">` +
      `<text x="30" y="88" text-anchor="middle" font-size="106" fill="currentColor" stroke="#0a0a0a" stroke-width="6" stroke-linejoin="round" paint-order="stroke">$</text>` +
      `</svg>`
    );
  }

  async function pricePatrol({ bar, header, room, done, until }) {
    const b = bar.getBoundingClientRect();
    const headerTop = header.getBoundingClientRect().top;
    const big = Math.min(130, Math.round(header.offsetHeight * 0.85)); // the $ at its most expensive
    const dw = big * 0.6;
    const h = 84;
    const th = h * 1.1; // thumbs-up pose, a touch taller as in the POW!
    const burstSize = 76;

    const layer = document.createElement('div');
    layer.className = 'bs-mfx-layer';
    layer.setAttribute('aria-hidden', 'true');
    const burst = document.createElement('div');
    burst.className = 'bs-mfx-burst';
    burst.innerHTML = burstMarkup('BAM!');
    const dollar = document.createElement('div');
    dollar.className = 'bs-mfx-dollar';
    dollar.innerHTML = dollarMarkup();
    const hero = sprite(cfg.sprites.punch);
    const thumbs = sprite(cfg.sprites.thumbs); // loaded up front for the mid-gag swap
    const bubble = document.createElement('div');
    bubble.className = 'bs-mfx-bubble';
    bubble.textContent = cfg.helloText || 'Hey! Fighting high prices daily!';
    layer.append(burst, dollar, hero, bubble);
    document.body.append(layer);

    // The fist lands on the $'s right flank. The $ sits ~38% along the bar,
    // nudged left when the thumbs-up and the bubble beside him would run off
    // the right edge of the window.
    const fist = dw * 0.36;
    const heroOffset = fist - h * RATIO.punch * 0.28;
    const rightOfDollar = heroOffset + th * RATIO.thumbs + 14 + bubble.offsetWidth;
    const cx = Math.max(b.left + dw / 2, Math.min(b.left + b.width * 0.38, window.innerWidth - 16 - rightOfDollar));
    const cy = b.top + b.height / 2;
    const left = cx + heroOffset;
    const top = cy - h * 0.41;
    const offscreen = window.innerWidth - left + 10;

    Object.assign(dollar.style, { width: `${dw}px`, height: `${big}px`, left: `${cx - dw / 2}px`, top: `${cy - big / 2}px` });
    Object.assign(hero.style, { height: `${h}px`, left: `${left}px`, top: `${top}px`, transform: `translateX(${offscreen}px)` });
    // BAM! goes up and left, where the big $ was, so his head doesn't cover it.
    Object.assign(burst.style, {
      width: `${burstSize}px`,
      height: `${burstSize}px`,
      left: `${cx - burstSize * 0.75}px`,
      top: `${Math.max(4, cy - burstSize * 1.05)}px`,
    });
    bubble.style.left = `${left + th * RATIO.thumbs + 14}px`;
    bubble.style.top = `${headerTop + Math.max(4, Math.round((room - bubble.offsetHeight) / 2))}px`;

    const bouncy = (frames) => frames.map((frame) => ({ easing: 'cubic-bezier(.3,1.6,.6,1)', ...frame }));
    const shrunk = 'translateX(-12px) scale(.24)';

    try {
      await until(Promise.all([loaded(hero), loaded(thumbs)]));
      if (done.signal.aborted) return;

      // 1. The price creeps up in three pumps, green to red, then trembles.
      await until(
        dollar.animate(
          bouncy([
            { transform: 'scale(0)', color: PRICE.low },
            { transform: 'scale(.24)', offset: 0.12 },
            { transform: 'scale(.24)', offset: 0.3 },
            { transform: 'scale(.5) rotate(-6deg)', offset: 0.42 },
            { transform: 'scale(.5) rotate(-6deg)', color: PRICE.rising, offset: 0.6 },
            { transform: 'scale(.76) rotate(5deg)', offset: 0.72 },
            { transform: 'scale(.76) rotate(5deg)', offset: 0.86 },
            { transform: 'scale(1) rotate(-2deg)', color: PRICE.high },
          ]),
          { duration: 1900, fill: 'forwards' }
        ).finished
      );
      if (done.signal.aborted) return;
      const tremble = dollar.animate(
        [{ transform: 'scale(1) rotate(-4deg)' }, { transform: 'scale(1.04) rotate(3deg)' }],
        { duration: 70, iterations: Infinity, direction: 'alternate' }
      );
      await until(sleep(350));
      if (done.signal.aborted) return;

      // 2. Sir Plus to the rescue: in from the right, fist first.
      await until(
        hero.animate(
          [
            { transform: `translate(${offscreen}px, -30px) rotate(-10deg)` },
            { transform: 'translate(-6px, 0) rotate(2deg)', offset: 0.75 },
            { transform: 'none' },
          ],
          { duration: 420, easing: 'cubic-bezier(.3,1.3,.5,1)', fill: 'both' }
        ).finished
      );
      tremble.cancel();
      if (done.signal.aborted) return;

      // 3. BAM! The $ squashes, spins and shrinks back down to a cheap green.
      burst.animate(
        [
          { opacity: 0, transform: 'scale(.2) rotate(-25deg)' },
          { opacity: 1, transform: 'scale(1.2) rotate(6deg)', offset: 0.6 },
          { opacity: 1, transform: 'none' },
        ],
        { duration: 300, easing: 'ease-out', fill: 'both' }
      );
      dollar.animate(
        [
          { transform: 'scale(1) rotate(-2deg)', color: PRICE.high },
          { transform: 'translateX(-8px) scale(1.2, .7) rotate(-10deg)', offset: 0.18 },
          { transform: 'translateX(-12px) scale(.16) rotate(18deg)', color: PRICE.low, offset: 0.7 },
          { transform: shrunk, color: PRICE.low },
        ],
        { duration: 560, easing: 'ease-out', fill: 'forwards' }
      );
      await until(sleep(800));
      if (done.signal.aborted) return;

      // 4. Thumbs-up and his line, feet kept on the same spot.
      burst.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(1.3)' }], { duration: 220, fill: 'forwards' });
      hero.src = thumbs.src;
      Object.assign(hero.style, { height: `${th}px`, top: `${top + h - th}px` });
      bubble.animate(
        [
          { opacity: 0, transform: 'scale(.3)' },
          { opacity: 1, transform: 'scale(1.08)', offset: 0.7 },
          { opacity: 1, transform: 'none' },
        ],
        { duration: 300, easing: 'ease-out', fill: 'forwards' }
      );
      dollar.animate([{ transform: shrunk }, { transform: `${shrunk} translateY(-40%)` }, { transform: shrunk }], {
        duration: 360,
        iterations: 3,
        easing: 'ease-in-out',
        delay: 200,
      });
      await until(sleep(2600));
      if (done.signal.aborted) return;

      // 5. Off to the next one, not back to sleep.
      bubble.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, fill: 'forwards' });
      dollar.animate([{ transform: shrunk }, { transform: 'translateX(-12px) scale(0)' }], {
        duration: 220,
        easing: 'ease-in',
        fill: 'forwards',
      });
      await until(
        hero.animate(
          [
            { transform: 'none' },
            { transform: 'translateY(-10px)', offset: 0.3 },
            { transform: `translate(${offscreen}px, -60px) rotate(8deg)` },
          ],
          { duration: 440, easing: 'ease-in', fill: 'forwards' }
        ).finished
      );
    } finally {
      if (done.signal.aborted) {
        await layer.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, fill: 'forwards' }).finished;
      }
      layer.remove();
    }
  }

  /* ---------- 3. Price punch (product pages) ---------- */

  const PUNCH_KEY = 'bsMascotPrice';
  let punchPlaying = false;

  // The main product's price block (snippets/price.liquid) when the selected
  // variant is on sale: the struck-through "their price", and the "You save"
  // chip, which carries the savings in data attributes.
  function saleOnPage() {
    if (cfg.page !== 'product') return null;
    const chip = document.querySelector('main [id^="price-"] .price--large.price--on-sale .bs-you-save[data-bs-save-amount]');
    const theirs = chip && chip.closest('.price__sale').querySelector('s.price-item--regular');
    return theirs && theirs.textContent.trim() ? { theirs, chip } : null;
  }

  function savingsLine(chip) {
    return (cfg.priceText || 'Saved you [savings]!')
      .replace(/\[savings\]/g, chip.dataset.bsSaveAmount)
      .replace(/\[percent\]/g, `${chip.dataset.bsSavePercent}%`);
  }

  // On phones the price often sits below the gallery: wait until the whole
  // price block is on screen, then play.
  function watchPrice() {
    const sale = saleOnPage();
    if (!sale || !due(PUNCH_KEY) || reduceMotion.matches) return;
    const onScreen = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        onScreen.disconnect();
        setTimeout(playPunch, 500);
      },
      { threshold: 1, rootMargin: '-10% 0px' }
    );
    onScreen.observe(sale.chip.closest('.price'));
  }

  async function playPunch() {
    if (!cfg || !cfg.price || punchPlaying || reduceMotion.matches || !due(PUNCH_KEY)) return;
    const sale = saleOnPage();
    if (!sale || !inViewport(sale.theirs)) return;
    const { theirs, chip } = sale;

    punchPlaying = true;
    markSeen(PUNCH_KEY);

    const vw = window.innerWidth;
    const small = vw < 750;
    const h = small ? 64 : 84;
    const th = h * 1.1;
    const burstSize = small ? 60 : 76;
    const r = theirs.getBoundingClientRect();
    const cs = getComputedStyle(theirs);

    const layer = document.createElement('div');
    layer.className = 'bs-mfx-layer bs-mfx-layer--page';
    layer.setAttribute('aria-hidden', 'true');
    // A copy of their price to blow up, so the real one never moves.
    const price = document.createElement('span');
    price.className = 'bs-mfx-price';
    price.textContent = theirs.textContent.trim();
    Object.assign(price.style, {
      left: `${r.left}px`,
      top: `${r.top}px`,
      height: `${r.height}px`,
      lineHeight: `${r.height}px`,
      fontFamily: cs.fontFamily,
      fontSize: cs.fontSize,
      fontWeight: cs.fontWeight,
      fontStyle: cs.fontStyle,
      letterSpacing: cs.letterSpacing,
      color: cs.color,
    });
    const strike = document.createElement('span');
    strike.className = 'bs-mfx-price__strike';
    price.append(strike);
    const burst = document.createElement('div');
    burst.className = 'bs-mfx-burst';
    burst.innerHTML = burstMarkup('BAM!');
    const hero = sprite(cfg.sprites.punch);
    const thumbs = sprite(cfg.sprites.thumbs);
    const bubble = document.createElement('div');
    bubble.className = 'bs-mfx-bubble';
    bubble.textContent = savingsLine(chip);
    layer.append(price, burst, hero, bubble);
    document.body.append(layer);

    // Swell as big as fits with Sir Plus beside it, up to 2.6x, growing to the
    // right so "Their price:" stays readable. His fist lands just past the
    // far end, so his arm doesn't cover the last letters.
    const w = price.offsetWidth;
    const reach = 6 + h * RATIO.punch * 0.72; // fist gap + hero width past his fist
    const grow = Math.max(1.5, Math.min(2.6, (vw - 12 - reach - r.left) / w));
    const at = (k) => `scale(${1 + (grow - 1) * k})`;
    const cy = r.top + r.height / 2;
    const left = r.left + w * grow + 6 - h * RATIO.punch * 0.28;
    const top = cy - h * 0.41;
    const thumbsTop = top + h - th;
    const offscreen = vw - left + 10;

    Object.assign(hero.style, { height: `${h}px`, left: `${left}px`, top: `${top}px`, transform: `translateX(${offscreen}px)` });
    Object.assign(burst.style, {
      width: `${burstSize}px`,
      height: `${burstSize}px`,
      left: `${Math.max(4, r.left + (w * grow) / 2 - burstSize * 0.6)}px`,
      top: `${Math.max(4, cy - (r.height * grow) / 2 - burstSize * 0.55)}px`,
    });

    // The bubble goes beside him, level with his face; on a phone there's
    // rarely room, so it sits over his head with the tail pointing down.
    const bw = bubble.offsetWidth;
    const bh = bubble.offsetHeight;
    const beside = left + th * RATIO.thumbs + 14;
    if (beside + bw <= vw - 8) {
      bubble.style.left = `${beside}px`;
      bubble.style.top = `${thumbsTop + th * 0.2 - bh / 2}px`;
    } else {
      const mid = left + (th * RATIO.thumbs) / 2;
      const bl = Math.max(8, Math.min(mid - bw / 2, vw - 8 - bw));
      bubble.classList.add('bs-mfx-bubble--up');
      bubble.style.left = `${bl}px`;
      bubble.style.top = `${thumbsTop - bh - 10}px`;
      bubble.style.setProperty('--tail', `${mid - bl}px`);
    }

    // Scrolling is fine: the layer follows the price (sticky product info
    // included). Tapping, typing, changing variant or resizing ends it early.
    const done = new AbortController();
    const finish = () => done.abort();
    const follow = () => {
      const now = theirs.getBoundingClientRect();
      layer.style.transform = `translate(${now.left - r.left}px, ${now.top - r.top}px)`;
    };
    window.addEventListener('scroll', () => requestAnimationFrame(follow), { passive: true, signal: done.signal });
    window.addEventListener('resize', () => window.innerWidth !== vw && finish(), { signal: done.signal });
    document.addEventListener('click', finish, { capture: true, signal: done.signal });
    document.addEventListener('keydown', finish, { signal: done.signal });
    const unsubscribe =
      typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
        ? subscribe(PUB_SUB_EVENTS.variantChange, finish)
        : null;
    const aborted = new Promise((resolve) => done.signal.addEventListener('abort', resolve, { once: true }));
    const until = (promise) => Promise.race([promise, aborted]);
    const bouncy = (frames) => frames.map((frame) => ({ easing: 'cubic-bezier(.3,1.6,.6,1)', ...frame }));

    theirs.style.visibility = 'hidden';
    try {
      await until(Promise.all([loaded(hero), loaded(thumbs)]));
      if (done.signal.aborted) return;

      // 1. Their price creeps up in three pumps and goes red, then trembles.
      await until(
        price.animate(
          bouncy([
            { transform: 'none', color: cs.color },
            { transform: `${at(0.35)} rotate(-3deg)`, offset: 0.25 },
            { transform: `${at(0.35)} rotate(-3deg)`, offset: 0.42 },
            { transform: `${at(0.7)} rotate(2deg)`, color: PRICE.rising, offset: 0.62 },
            { transform: `${at(0.7)} rotate(2deg)`, offset: 0.78 },
            { transform: `${at(1)} rotate(-1deg)`, color: PRICE.high },
          ]),
          { duration: 1600, fill: 'forwards' }
        ).finished
      );
      if (done.signal.aborted) return;
      const tremble = price.animate(
        [{ transform: `${at(1)} rotate(-2deg)` }, { transform: `${at(1.04)} rotate(1.5deg)` }],
        { duration: 70, iterations: Infinity, direction: 'alternate' }
      );
      await until(sleep(300));
      if (done.signal.aborted) return;

      // 2. Sir Plus to the rescue: in from the right, fist first.
      await until(
        hero.animate(
          [
            { transform: `translate(${offscreen}px, -30px) rotate(-10deg)` },
            { transform: 'translate(-6px, 0) rotate(2deg)', offset: 0.75 },
            { transform: 'none' },
          ],
          { duration: 420, easing: 'cubic-bezier(.3,1.3,.5,1)', fill: 'both' }
        ).finished
      );
      tremble.cancel();
      if (done.signal.aborted) return;

      // 3. BAM! It squashes and shrinks back into place, gets struck through
      // again, and the real one takes over while the "You save" chip pops.
      burst.animate(
        [
          { opacity: 0, transform: 'scale(.2) rotate(-25deg)' },
          { opacity: 1, transform: 'scale(1.2) rotate(6deg)', offset: 0.6 },
          { opacity: 1, transform: 'none' },
        ],
        { duration: 300, easing: 'ease-out', fill: 'both' }
      );
      await until(
        price.animate(
          [
            { transform: `${at(1)} rotate(-1deg)`, color: PRICE.high },
            { transform: `scale(${grow * 1.12}, ${grow * 0.62}) rotate(-4deg)`, offset: 0.2 },
            { transform: 'scale(.8)', color: cs.color, offset: 0.7 },
            { transform: 'none', color: cs.color },
          ],
          { duration: 520, easing: 'ease-out', fill: 'forwards' }
        ).finished
      );
      if (done.signal.aborted) return;
      await until(
        strike.animate([{ transform: 'scaleX(0)' }, { transform: 'none' }], {
          duration: 180,
          easing: 'ease-out',
          fill: 'forwards',
        }).finished
      );
      theirs.style.visibility = '';
      price.remove();
      chip.animate([{ transform: 'none' }, { transform: 'scale(1.18) rotate(-2deg)' }, { transform: 'none' }], {
        duration: 420,
        easing: 'ease-out',
      });
      await until(sleep(250));
      if (done.signal.aborted) return;

      // 4. Thumbs-up and the savings, feet kept on the same spot.
      burst.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(1.3)' }], { duration: 220, fill: 'forwards' });
      hero.src = thumbs.src;
      Object.assign(hero.style, { height: `${th}px`, top: `${thumbsTop}px` });
      bubble.animate(
        [
          { opacity: 0, transform: 'scale(.3)' },
          { opacity: 1, transform: 'scale(1.08)', offset: 0.7 },
          { opacity: 1, transform: 'none' },
        ],
        { duration: 300, easing: 'ease-out', fill: 'forwards' }
      );
      await until(sleep(2600));
      if (done.signal.aborted) return;

      // 5. Off to the next one.
      bubble.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, fill: 'forwards' });
      await until(
        hero.animate(
          [
            { transform: 'none' },
            { transform: 'translateY(-10px)', offset: 0.3 },
            { transform: `translate(${offscreen}px, -60px) rotate(8deg)` },
          ],
          { duration: 440, easing: 'ease-in', fill: 'forwards' }
        ).finished
      );
    } finally {
      theirs.style.visibility = '';
      if (done.signal.aborted) {
        await layer.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, fill: 'forwards' }).finished;
      }
      done.abort();
      if (unsubscribe) unsubscribe();
      layer.remove();
      punchPlaying = false;
    }
  }

  /* ---------- start-up ---------- */

  function start() {
    if (!cfg) return;
    setTimeout(() => {
      (window.requestIdleCallback || setTimeout)(() => {
        playHello();
        if (cfg.price) watchPrice();
      });
    }, 1200);
    if (cfg.pow) {
      // ~25 KB, fetched after the page has settled so the first POW never flashes.
      sprite(cfg.sprites.punch);
      sprite(cfg.sprites.thumbs);
    }
  }

  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });

  // Theme editor: replay the hello or the price punch when the header or the
  // product section is re-rendered.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.bs-header')) {
      cfg = readConfig();
      playHello();
    } else if (cfg && cfg.price && event.target.querySelector('[id^="price-"]')) {
      watchPrice();
    }
  });
})();
